import { useContext } from "react";
import { AppContext } from "../app/store";
import { hasGlobalSession } from "./loginHelpers";
import {
  Action,
  ActionType,
  AppState,
  CoverageSelectorCoverageType,
  CoverageSelectorSelectedValues,
  Pharmacy,
  PharmacyType,
  PlanType,
  PrescriptionDrug,
} from "../@types";
import routes from "../app/routes";
import { hasMailOrderPharmacy } from "../app/store";
import { useIsBEDAPDown } from ".";

export type LiCoverageSelectorRoutingParams = {
  pharmacies: Pharmacy[];
  prescriptions: PrescriptionDrug[];
  pharmacyType: PharmacyType;
  isBEDAPDown: boolean;
};

export type CoverageSelectorRoutingParams = {
  isBEDAPDown: boolean;
  isLoggedIn: boolean;
  pharmacies: Pharmacy[];
  prescriptions: PrescriptionDrug[];
  pharmacyType: PharmacyType;
};

/**
 * Determines some of the possible next routes from the coverage selector,
 * based on whether they're LI or anonymous, the status of BEDAP, and the current
 * status of the bene's pharmacies and prescription drugs
 */
export const getNextRouteFromCoverageSelector = ({
  isBEDAPDown,
  isLoggedIn,
  pharmacies,
  prescriptions,
  pharmacyType,
}: CoverageSelectorRoutingParams) => {
  const hasMailOrderPharmacies = hasMailOrderPharmacy({
    pharmacyType,
  } as AppState);
  const pharmacyCount = pharmacies.length + (hasMailOrderPharmacies ? 1 : 0);
  const drugsCount = prescriptions.length;
  const isMissingPharmsOrDrugs = pharmacyCount === 0 || drugsCount === 0;
  const baseRoutes = isLoggedIn ? routes.summary : routes;
  if (!isLoggedIn || isBEDAPDown) {
    return baseRoutes.lisQuestions;
  } else {
    return isMissingPharmsOrDrugs
      ? baseRoutes.drugSearchPreferences
      : baseRoutes.searchResults;
  }
};

/**
 * Provides a map of destination routes for a bene based on chosen coverage type
 */
export const getCoverageSelectorDestinationMap = ({
  isBEDAPDown,
  isLoggedIn,
  pharmacies,
  prescriptions,
  pharmacyType,
}: CoverageSelectorRoutingParams): Record<
  CoverageSelectorCoverageType,
  string
> => {
  const nextRoute = getNextRouteFromCoverageSelector({
    isBEDAPDown,
    isLoggedIn,
    pharmacies,
    prescriptions,
    pharmacyType,
  });
  return {
    [CoverageSelectorCoverageType.MAPD]: nextRoute,
    [CoverageSelectorCoverageType.PDP]: nextRoute,
    [CoverageSelectorCoverageType.MedigapPolicy]: routes.medigap.landingPage,
    [CoverageSelectorCoverageType.learnMore]: routes.coverageWizard.options,
  };
};

/**
 * Hook that provides a map of destination routes for a bene based on chosen
 * coverage type
 */
export const useCoverageSelectorDestinationMap = () => {
  const {
    state: { pharmacies, pharmacyType, prescriptions },
    state,
  } = useContext(AppContext);
  const isBEDAPDown = useIsBEDAPDown();
  const isLoggedIn = hasGlobalSession(state);
  return getCoverageSelectorDestinationMap({
    isBEDAPDown,
    isLoggedIn,
    pharmacies,
    pharmacyType,
    prescriptions,
  });
};

/**
 * Populate initial coverage selector form values
 */
export const getInitialFormValues = ({
  beneficiary,
  county,
  zipcode,
}: {
  beneficiary: AppState["beneficiary"];
  county: AppState["county"];
  zipcode: AppState["zipcode"];
}) => ({
  zipcode: zipcode || beneficiary?.mailing_address.zipcode,
  fips: county?.fips || beneficiary?.mailing_address.fips_state_county || "",
});

/**
 * an easy way to grab initial values from state
 * either from a top level attribute or falling back to beneficiary for logged in users.
 */
export function useInitialFormValues() {
  const {
    state: { beneficiary, county, zipcode },
  } = useContext(AppContext);
  const initialFormValues = getInitialFormValues({
    beneficiary,
    county,
    zipcode,
  });

  return {
    initialFormValues,
  };
}

const planTypeMap: Record<CoverageSelectorCoverageType, PlanType | undefined> =
  {
    [CoverageSelectorCoverageType.MAPD]: PlanType.MAPD,
    [CoverageSelectorCoverageType.PDP]: PlanType.PDP,
    [CoverageSelectorCoverageType.MedigapPolicy]: undefined,
    [CoverageSelectorCoverageType.learnMore]: undefined,
  };

export const updateStates = (
  coverageSelectorValues: CoverageSelectorSelectedValues,
  dispatch: React.Dispatch<Action>
): void => {
  dispatch({
    type: ActionType.UPDATE_YEAR,
    payload: coverageSelectorValues.coverageYear.toString(),
  });
  dispatch({
    type: ActionType.UPDATE_COUNTY,
    payload: coverageSelectorValues.county,
  });
  dispatch({
    type: ActionType.UPDATE_FIPS,
    payload: coverageSelectorValues.county?.fips,
  });
  dispatch({
    type: ActionType.UPDATE_ZIPCODE,
    payload: coverageSelectorValues.zipcode,
  });
  dispatch({
    type: ActionType.UPDATE_PHARMACY_ADDRESS,
    payload: "",
  });

  if (coverageSelectorValues.coverageType) {
    const planType = planTypeMap[coverageSelectorValues.coverageType];
    if (planType) {
      dispatch({
        type: ActionType.UPDATE_PLAN_TYPE,
        payload: planType,
      });
    }
  }
};
